import type { MouseEvent, ReactNode } from "react";
import { Button } from "@jkast90/kast-common/ui";
import { useOffline } from "../../hooks/useOffline";
import { OFFLINE_WRITE_MESSAGE } from "../../utils/connectivity";

/** An extra button shown before Cancel. */
export interface DialogAction {
  text: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

/**
 * A dialog's button row, for `BaseDialog`'s `footer`: kast-common's Buttons
 * (the submit shows a spinner and "Saving..." while `isLoading`, and ignores
 * clicks until it is done) with the app's offline gate. The row sits outside
 * the body's form, so a submit names its form through `form`.
 */
export default function DialogActions({
  onCancel,
  onSubmit,
  form,
  cancelText = "Cancel",
  submitText = "Save",
  submitDisabled = false,
  cancelDisabled = false,
  isLoading = false,
  loadingText = "Saving...",
  additionalActions = [],
  needsConnection = false,
  "data-testid": testId = undefined,
}: {
  onCancel: () => void;
  onSubmit: (event: MouseEvent<HTMLButtonElement>) => void;
  /** The id of the form the submit sends (it is in the dialog's footer, outside the form). */
  form?: string;
  cancelText?: string;
  submitText?: string;
  submitDisabled?: boolean;
  cancelDisabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  additionalActions?: DialogAction[];
  /**
   * The submit writes to the API: offline it is disabled, with a short note
   * saying why, and the dialog stays open so nothing typed is lost.
   */
  needsConnection?: boolean;
  "data-testid"?: string;
}) {
  const { isReadOnly } = useOffline();
  const offline = needsConnection && isReadOnly;
  return (
    <>
      {offline && (
        <span className="kui-grow kui-muted kui-small" role="status">
          {OFFLINE_WRITE_MESSAGE}
        </span>
      )}
      {additionalActions.map((action, index) => (
        <Button key={index} onClick={action.onClick} disabled={action.disabled}>
          {action.text}
        </Button>
      ))}
      <Button onClick={onCancel} disabled={cancelDisabled}>
        {cancelText}
      </Button>
      <Button
        type="submit"
        form={form}
        variant="primary"
        onClick={onSubmit}
        disabled={submitDisabled || offline}
        title={offline ? OFFLINE_WRITE_MESSAGE : undefined}
        data-testid={testId}
        loading={isLoading}
        loadingLabel={loadingText}
      >
        {submitText}
      </Button>
    </>
  );
}
